import * as THREE from 'three';

import { FEATURE_COLOR, buildFeatureOverlay } from './digital-twin-geometry.js';
import type { FrameWorkQueue } from './frame-work-queue.js';
import { OverlayGeometryBatch } from './overlay-geometry-batch.js';
import type { PreparationService } from './preparation-protocol.js';
import { TerrainDraper } from './terrain-drape.js';
import { DigitalTwinFeature } from '../../types/digital-twin.js';
import type { FeatureOverlay } from '../../types/terrain-types.js';

/**
 * Terrain state the overlays need in order to drape themselves onto the
 * surface. Supplied by the terrain manager that owns the tiles.
 */
export interface OverlayTerrainContext {
  /** Absolute LV95 origin the terrain group is anchored at. */
  requireOrigin(): THREE.Vector3;
  /** Whether an origin has been resolved yet. */
  hasOrigin(): boolean;
  /** Shared, time-budgeted height resolution for all overlay layers. */
  draper: TerrainDraper;
  preparation: PreparationService;
  workQueue: FrameWorkQueue;
  /** Canvas backing the renderer, needed to size pixel-wide lines. */
  rendererCanvas(): HTMLCanvasElement | undefined;
}

/**
 * Renders digital twin features as overlays draped on the terrain surface, and
 * answers proximity queries against them.
 *
 * Owns overlay lifecycle and delegates height resolution to the shared draper;
 * GeoJSON to THREE.js geometry construction lives in `digital-twin-geometry`.
 */
export class DigitalTwinOverlay {
  readonly group: THREE.Group;

  private overlays: FeatureOverlay[] = [];
  private batch?: OverlayGeometryBatch;
  private preparation = new AbortController();

  private visible = false;
  private terrainEnabled = false;
  /** Features hidden individually, e.g. while their point cloud is loaded. */
  private readonly hiddenGuids = new Set<string>();

  private lineWidth = 4; // pixels
  private readonly lineResolution = new THREE.Vector2(1, 1);

  constructor(private readonly terrain: OverlayTerrainContext) {
    this.group = new THREE.Group();
    this.group.name = 'DigitalTwinFeatures';
    this.group.visible = false;
  }

  /**
   * Replace the currently displayed digital twin feature overlays.
   * Each feature is a GeoJSON geometry (Point, LineString, Polygon or their
   * Multi* variants).
   */
  async setFeatures(features: DigitalTwinFeature[]): Promise<void> {
    this.clear();
    const signal = this.preparation.signal;

    // Anchor at the same LV95 origin as the terrain so overlays align exactly
    // with the surface.
    const origin = this.terrain.requireOrigin();
    this.group.position.set(origin.x, origin.y, origin.z);

    const lineStyle = { width: this.lineWidth, resolution: this.getRendererResolution() };
    const batch = new OverlayGeometryBatch(lineStyle, FEATURE_COLOR, this.terrain.workQueue);
    this.batch = batch;
    this.group.add(batch.group);

    // Bound structured-clone work and apply backpressure instead of posting the
    // entire tenant's GeoJSON or installing all prepared features in one turn.
    try {
      for (let start = 0; start < features.length; start += 32) {
        const prepared = await this.terrain.preparation.features(
          features.slice(start, start + 32),
          [origin.x, origin.y, origin.z],
          signal,
        );
        const install = function* (owner: DigitalTwinOverlay): Generator<void> {
          for (const feature of prepared) {
            const overlay = yield* buildFeatureOverlay(feature, batch);
            overlay.group.visible = !owner.hiddenGuids.has(feature.guid);
            batch.setFeatureVisible(feature.guid, overlay.group.visible);
            owner.overlays.push(overlay);
            owner.terrain.draper.register(
              overlay,
              () => owner.group.visible && overlay.group.visible,
            );
            yield;
          }
        };
        await this.terrain.workQueue.run(install(this), signal);
      }
      this.update();
    } catch (error) {
      if (this.batch === batch) this.clear();
      throw error;
    }
  }

  /**
   * Remove all digital twin feature overlays from the scene.
   */
  clear(): void {
    this.preparation.abort();
    this.preparation = new AbortController();
    for (const overlay of this.overlays) {
      this.terrain.draper.unregister(overlay);
    }
    if (this.batch) {
      this.group.remove(this.batch.group);
      this.batch.dispose();
      this.batch = undefined;
    }
    this.overlays = [];
  }

  /**
   * Find the digital twin feature nearest to the given absolute LV95 position,
   * within `maxDistance` metres. Used by the dynamic point cloud loader to
   * decide which model to load as the camera approaches it.
   *
   * The distance is measured against the feature's bounding box, so long lines
   * and large polygons are matched as soon as any part of them is close,
   * instead of only near their centroid.
   */
  findNearest(
    x: number,
    y: number,
    maxDistance: number,
  ): { guid: string; name: string; distance: number; lon: number; lat: number } | null {
    let nearest: FeatureOverlay | null = null;
    let nearestDistance = maxDistance;

    for (const overlay of this.overlays) {
      const dx = Math.max(overlay.minX - x, 0, x - overlay.maxX);
      const dy = Math.max(overlay.minY - y, 0, y - overlay.maxY);
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= nearestDistance) {
        nearestDistance = distance;
        nearest = overlay;
      }
    }
    if (!nearest) return null;

    return {
      guid: nearest.guid,
      name: nearest.name,
      distance: nearestDistance,
      lon: nearest.repLon,
      lat: nearest.repLat,
    };
  }

  /**
   * Show or hide the digital twin feature markers.
   */
  setVisible(visible: boolean): void {
    this.visible = visible;
    this.group.visible = this.terrainEnabled && visible;
    if (visible) {
      this.update();
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Track the terrain's enabled state, which gates overlay visibility.
   */
  setTerrainEnabled(enabled: boolean): void {
    this.terrainEnabled = enabled;
    this.group.visible = enabled && this.visible;
    if (!enabled) this.preparation.abort();
  }

  /**
   * Show or hide a single digital twin feature by GUID. Used to hide the
   * overlay of a feature while its point cloud is displayed instead, so the
   * overlay geometry does not sit on top of the real data.
   */
  setFeatureVisible(guid: string, visible: boolean): void {
    if (visible) {
      this.hiddenGuids.delete(guid);
    } else {
      this.hiddenGuids.add(guid);
    }

    for (const overlay of this.overlays) {
      if (overlay.guid === guid) {
        overlay.group.visible = visible;
      }
    }
    this.batch?.setFeatureVisible(guid, visible);
  }

  /**
   * Set the stroke width (in pixels) used for digital twin line features.
   */
  setLineWidth(width: number): void {
    this.lineWidth = width;
    this.batch?.setLineWidth(width);
  }

  dispose(): void {
    this.clear();
    this.lineResolution.set(1, 1);
  }

  /** Height work is scheduled once per frame by the terrain manager. */
  update(): void {
    if (!this.visible || this.overlays.length === 0 || !this.terrain.hasOrigin()) {
      return;
    }

    this.batch?.setResolution(this.getRendererResolution());
  }

  flush(camera?: THREE.Camera): void {
    if (this.group.visible) this.batch?.flush(camera);
  }

  /**
   * Current canvas size. Line2 needs it to convert its pixel width into clip
   * space, so it has to follow renderer resizes.
   */
  private getRendererResolution(): THREE.Vector2 {
    const canvas = this.terrain.rendererCanvas();
    if (canvas) {
      this.lineResolution.set(canvas.clientWidth || 1, canvas.clientHeight || 1);
    }
    return this.lineResolution;
  }
}
